import React, { useState } from "react";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import "./quotation.css";
import logo from "../../../assets/Logo/logo_Black.png";
import MapCurrency from "../../../utils/Mappers/currency";
import { toWords } from "number-to-words";

const QuotationPdf = ({documentData}) => {
  const [isGenerating, setIsGenerating] = useState(false);

  const subTotal = documentData ? parseFloat(documentData.sub_total) : 0;
  const grandTotal = documentData ? parseFloat(documentData.total) : 0;
  const pra_percent = documentData ? parseFloat(documentData.pra_percent) : 0;

  // Product name mapping
  const productNameMapping = {
    "SPCAD_GSTARCAD": "Spcad For GstarCAD",
    "SPCAD_CADMATE": "Spcad For CADMATE",
    "SPCAD_BRICSCAD": "Spcad For BricsCAD",
    "SPCAD_AUTOCAD": "Spcad For AutoCAD",
    "SPCAD_PCAD": "Spcad For ProgeCAD",
    "SPCAD_ZWCAD": "Spcad For ZWCAD",
  };

  // Function to download as PDF
  const downloadPDF = async () => {
    setIsGenerating(true);
  
    const element = document.querySelector(".document-container");
    const downloadButton = document.querySelector(".download-button");
    downloadButton.style.visibility = "hidden";
  
    element.classList.add("pdf-export");
  
    try {
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
        compress: true,
      });
  
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
  
      // Convert the HTML element to a canvas
      const canvas = await html2canvas(element, {
        scale: 2, // Adjust scale for better resolution
        scrollX: 0,
        scrollY: 0,
        useCORS: true,
        backgroundColor: "#ffffff",
      });
  
      const imgData = canvas.toDataURL("image/png");
      const imgWidth = pdfWidth;
      const imgHeight = (canvas.height * pdfWidth) / canvas.width;
  
      // Ensure the content fits on a single page
      if (imgHeight > pdfHeight) {
        console.warn("Content exceeds A4 page height. Adjust the layout to fit on a single page.");
      }
  
      // Add the image to the PDF
      pdf.addImage(imgData, "PNG", 0, 0, imgWidth, imgHeight);
  
      pdf.save(`Quotation_${documentData.reference_number}.pdf`);
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("An error occurred while generating the PDF. Please try again.");
    } finally {
      element.classList.remove("pdf-export");
      downloadButton.style.visibility = "visible";
      setIsGenerating(false);
    }
  };

  return (
    <div className="document-container">
      {/* Header Section */}
      <header>
        <div className="header-top">
          <div className="left-header">
            <img src={logo} alt="CADOMATION Logo" className="logo" />
            <p style={{ margin: "0px" }}>www.cadomation.com</p>
            <p style={{ margin: "0px" }}>sales@cadomation.com</p>
          </div>
          <div className="right-header">
        <h3>QUOTATION</h3>
      </div>
        </div>
      </header>
      {/* Client Details Section */}
      <section className="client-info">
        <div className="client-info-header">
          <div>
            <h3>Client Details</h3>
            <p>
              <strong>{documentData?.user_profile?.name}</strong>
            </p>
            <p>{documentData?.user_profile?.designation}</p>
            <p>{documentData?.user_profile?.organization}</p>
            <p>{documentData?.user_profile?.address_line_1}</p>
            <p>{documentData?.user_profile?.address_line_2}</p>
            <p>Email: {documentData?.user_profile?.email}</p>
            <p>Contact: {documentData?.user_profile?.phone}</p>
          </div>
          <div className="document-details">
            <p>
              <strong>QTN #</strong> {documentData?.reference_number}
            </p>
            <p>
              <strong>Date:</strong>{" "}
              {documentData?.created_at
                ? new Date(documentData.created_at).toLocaleDateString("en-GB", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                  })
                : ""}
            </p>
          </div>
        </div>
      </section>

      {/* Document Body Section */}
      <section className="document-body">
        <table>
          <thead>
            <tr>
              <th>DESCRIPTION OF ITEM</th>
              <th>NO. OF LICENSES</th>
              <th>END USER PRICE <br/>({MapCurrency(documentData?.currency)})</th>
              {documentData?.document_items?.some((item) => item.discount && item.discount > 0) && (
                <th>DISCOUNT (%)</th>
              )}
              <th>TOTAL <br/>({MapCurrency(documentData?.currency)})</th>
            </tr>
          </thead>
          <tbody>
            {documentData?.document_items?.map((item) => {
              // Map the product name
              const customProductName = productNameMapping[item.product_name] || item.product_name;

              return (
                <tr key={item.id}>
                  <td>{customProductName ? customProductName : "-" }</td>
                  <td>{item.quantity ? item.quantity : "-"}</td>
                  <td>{item.price ? item.price?.toFixed(2) : "-"}</td>
                  {documentData?.document_items.some((i) => i.discount && i.discount > 0) && (
                    <td>{item.discount ? item.discount.toFixed(2) : ""}</td>
                  )}
                  <td>{item.total? item.total?.toFixed(2):"-"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className="totals">
          {pra_percent > 0 && (
            <>
              <p>
                <strong>SUB-TOTAL:</strong> {subTotal.toFixed(2)}
              </p>
              <p>
                <strong>PRA @ {pra_percent}%:</strong> {(subTotal * pra_percent / 100).toFixed(2)}
              </p>
            </>
          )}
          <p>
            <strong>TOTAL ({MapCurrency(documentData?.currency)}):</strong> {grandTotal.toFixed(2)}
          </p>
        </div>
        <p><strong>Amount in words:</strong> <em>{toWords(Math.floor(documentData?.total))} only</em></p>
      </section>

      {/* Terms & Conditions Section */}
      <section className="terms">
        <h3>Terms & Conditions:</h3>
        <ul>
          {documentData?.terms_and_conditions
            ?.split("\n")
            .map((term, index) => (
              <li key={index}>{term.trim()}</li>
            ))}
        </ul>
      </section>

      {/* Footer Section */}
      <footer className="footer-section">
        <p className="thank-you-note" style={{ textAlign: "center" }}>
          We trust you will find the quote satisfactory and look forward to your business. Please feel free to contact us in case of any question(s).
        </p>
        <div className="footer-contact">
          <p style={{ textAlign: "center" }}>
            <em>This is a system-generated document and does not require any signatures.</em>
          </p>
        </div>
      </footer>

      {isGenerating ? (
        <p className="loading-message">Generating PDF... Please wait.</p>
      ) : (
        <button onClick={downloadPDF} className="download-button">
          Download as PDF
        </button>
      )}
    </div>
  );
};

export default QuotationPdf;
